import type { Expression } from './expression.ts';
import { expressionForCue } from './expression.ts';

/**
 * How long a cue holds the face before the turn state takes it back.
 *
 * A reaction is a moment, not a mode: holding it until the next cue would
 * leave the face celebrating through the tutor's entire reply, which reads as
 * the avatar having got stuck rather than as an answer to what was said.
 */
export const HOLD_MS = 2200;

/**
 * The expression to show: a recent cue if there is one, else the turn state.
 *
 * `now` and `cueAt` must share a timebase — the avatar passes the shared
 * clock's rAF timestamp and `SessionView` records `performance.now()`, which
 * is the same one. The elapsed check is written so that a negative difference
 * falls back rather than holding: mixing timebases is the mistake most likely
 * to be made here, and pinning the face on one cue for ever is the worst way
 * to find out.
 */
export function heldExpression(cue: string | null, cueAt: number, now: number, fallback: Expression): Expression {
  if (cue === null) return fallback;
  const elapsed = now - cueAt;
  if (!(elapsed >= 0 && elapsed <= HOLD_MS)) return fallback;
  return expressionForCue(cue) ?? fallback;
}

/**
 * How long a cue has left to hold, or `null` when there is nothing to wait for.
 *
 * Under reduced motion the clock stops, so nothing re-renders the avatar when
 * a hold ends; this is how long its timer waits instead. Same timebase rule as
 * `heldExpression`.
 */
export function holdRemaining(cue: string | null, cueAt: number, now: number): number | null {
  if (cue === null) return null;
  const left = cueAt + HOLD_MS - now;
  return left > 0 ? left : null;
}
